import { Notice } from "../../models/notice";
import { useTranslation } from "react-i18next";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { NoticesHeader } from "./noticesHeader";
import { Button } from "../common/button";
import { toast } from "react-toastify";
import { useNotice } from "../../hooks/useNotice";
import { Dialog, DialogActions, DialogMessage } from "../common/dialog";
import { uploadImage } from "../../utils/imageUploader";
import brokenImg from "../../assets/broken.png";

interface NoticeEditorProps {
  initialNotice: Notice | null;
  onSave?: (notice: Notice) => void;
  onCancel?: () => void;
  artistId: number;
  eventId: number;
  rewardId: number;
}

const MAX_IMAGES = 3;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
];

export function NoticeEditorComp({
  initialNotice,
  onSave,
  onCancel,
  artistId,
  eventId,
  rewardId,
}: NoticeEditorProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const { leaveNotice, isPending } = useNotice(artistId, eventId, rewardId);

  const leaveNoticeRef = useRef<HTMLDialogElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialNotice) {
      setTitle(initialNotice.title);
      setContent(initialNotice.content);
      setIsPrimary(initialNotice.isPrimary);
      setImages(initialNotice.images ?? []);
    }
  }, [initialNotice]);

  const onImageChanged = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (images.length >= MAX_IMAGES || !ALLOWED_IMAGE_TYPES.includes(file.type) || file.size > 20 * 1024 * 1024) {
      toast(t("fanevent.notice.image.helper", { n: MAX_IMAGES }));
      return;
    }

    const url = await uploadImage(file);
    setImages((prev) => [...prev, url]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const attachImage = () => {
    if (images.length >= MAX_IMAGES) {
      toast(t("fanevent.notice.image.helper", { n: MAX_IMAGES }));
      return;
    }
    fileInputRef.current?.click();
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    const notice = {
      id: initialNotice?.id || null,
      isPrimary,
      title,
      content,
      images,
    } as Notice;
    await leaveNotice(notice);
    if (onSave) {
      onSave(notice);
    }
  };

  return (
    <div className={"flex flex-col overflow-hidden"}>
      <NoticesHeader
        title={t(!!initialNotice ? "notice_edit_title" : "notice_add_title")}
        onClose={onCancel}
      />
      <div className={"p-4 flex-grow overflow-y-auto"}>
        {!initialNotice && (
          <div
            role={"alert"}
            className={"alert alert-info rounded-md text-xs mb-6 grid-flow-col"}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="stroke-current shrink-0 w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span className={"text-left"}>{t("notice_add_guide")}</span>
          </div>
        )}
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            leaveNoticeRef.current?.showModal();
          }}
          className={"space-y-3"}
        >
          <div className={"form-control"}>
            <label className={"label cursor-pointer justify-start gap-3"}>
              <input
                type={"checkbox"}
                className={"checkbox checkbox-primary"}
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
              />
              <div>
                <span className={"label-text text-sm"}>
                  {t("notice_add_important_title")}
                </span>
                <p className={"text-xs text-gray-500"}>
                  {t("notice_add_important_guide")}
                </p>
              </div>
            </label>
          </div>
          <div>
            <label htmlFor={"title"} className={"label"}>
              <span className={"label-text"}>
                {t("notice_add_noticetitle")}
              </span>
            </label>
            <label className={"input input-bordered flex items-center gap-2"}>
              <input
                id={"title"}
                type={"text"}
                placeholder={t("notice_add_noticetitle_placeholder")}
                className={"grow text-sm"}
                value={title || ""}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
              />
              <span className={"label-text-alt text-gray-300"}>
                {title?.length || 0}/200
              </span>
            </label>
          </div>
          <div>
            <label htmlFor={"content"} className={"label justify-start"}>
              <span className={"label-text"}>
                {t("notice_add_noticecontents")}
              </span>
              <span className={"badge badge-sm badge-info ml-2"}>
                {t("common_badge_required")}
              </span>
            </label>
            <div className={"relative"}>
              <textarea
                id={"content"}
                placeholder={t("notice_add_noticecontents_placeholder")}
                className={
                  "textarea textarea-bordered grow w-full text-sm h-96"
                }
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={30000}
                required
              ></textarea>
              <span
                className={
                  "absolute right-2 bottom-2 label-text-alt ml-auto text-gray-300"
                }
              >
                {content.length}/30000
              </span>
            </div>
          </div>
          <div>
            <label className={"label"} htmlFor={"image"}>
              <span className={"label-text"}>{t("notice_add_image")}</span>
              <span className={"label-text-alt text-gray-400"}>
                {images.length}/{MAX_IMAGES}
              </span>
            </label>
            {images.length < MAX_IMAGES && (
              <button
                type={"button"}
                className={"btn btn-outline w-36 rounded-md"}
                onClick={attachImage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 4.5v15m7.5-7.5h-15"
                  />
                </svg>
                {t("notice_add_image")}
              </button>
            )}
            <input
              id={"image-upload"}
              type={"file"}
              accept={"image/jpeg, image/png, image/heic, image/heif"}
              className={"hidden"}
              onChange={onImageChanged}
              ref={fileInputRef}
            />
            {images.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {images.map((image, index) => (
                  <div
                    key={`${image}-${index}`}
                    className="border border-base-300 rounded-md relative w-24 h-24 flex items-center justify-center"
                  >
                    <img
                      src={`${image}?type=s110`}
                      className="w-full object-cover overflow-hidden h-full rounded-md"
                      onError={(e) => {
                        e.currentTarget.src = brokenImg;
                        e.currentTarget.onerror = null;
                      }}
                      alt={t("notice_add_image")}
                    />
                    <button
                      type={"button"}
                      className={
                        "absolute -top-2 -right-2 btn btn-circle btn-xs bg-primary text-white border-none z-20"
                      }
                      onClick={() => removeImage(index)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="size-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18 18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button
            type={"submit"}
            className={"btn btn-primary w-full rounded-md"}
            loading={isPending}
          >
            {t(!!initialNotice ? "notice_btn_edit" : "notice_btn_register")}
          </Button>
        </form>
        <Dialog ref={leaveNoticeRef}>
          <DialogMessage>
            {t(
              !!initialNotice ? "dialog_notice_edit" : "dialog_notice_register",
            )}
          </DialogMessage>
          <DialogActions>
            <div className={"flex gap-2 justify-end w-full"}>
              <button className={"btn basis-1/4"}>
                {t("common_dialog_cancel")}
              </button>
              <Button
                type={"button"}
                className={"btn btn-primary basis-1/4"}
                onClick={onSubmit}
              >
                {t("common_dialog_yes")}
              </Button>
            </div>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
